import React, { FC, Fragment, useState } from 'react';
import {
    Avatar,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
    Box,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FileDropBoxArea from 'components/shared/file-drop-box-area/file-drop-box-area.component';
import Loader from 'components/shared/loader/loader.component';
import axios from 'axios';
import { CONSTANTS } from 'config/constants';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

type ImportOrderFromExternalSystemDialogProps = {
    open: boolean;
    onCloseDialog: () => void;
    labId?: string;
};
const ImportOrderFromExternalSystemDialog: FC<ImportOrderFromExternalSystemDialogProps> = ({
    open,
    onCloseDialog,
    labId,
}: ImportOrderFromExternalSystemDialogProps) => {
    const { t: translate } = useTranslation(['users', 'orders', 'common']);
    const [files, setFiles] = useState<any>([]);
    const [selectedTypeOfImport, setSelectedTypeOfImport] = useState('');
    const [resErrorMessage, setResErrorMessage] = useState('');
    const [fileSuccessfullyImported, setFileSuccessfullyImported] = useState(false);
    const [uploading, setUploading] = useState(false);
    const accept = { 'text/csv': [] };

    const handleChange = async (currentFiles: any) => {
        setFileSuccessfullyImported(false);
        setResErrorMessage('');
        if (!currentFiles || currentFiles.length === 0) {
            return;
        }
        setFiles([...files, ...currentFiles]);
    };

    const handleImport = async () => {
        try {
            setUploading(true);
            const formData = new FormData();
            for (const file of files) {
                formData.append('files', file, file.importType);
            }
            // super-admin-backend's REST API, authenticated by the session cookie.
            const url = `${CONSTANTS.REST_API_URI}/organizations/${labId}/uploads/orders`;
            const response = await axios.post(url, formData, {
                withCredentials: true,
                // Required by super-admin-backend: forces a CORS preflight before the cookie is used.
                headers: { 'apollo-require-preflight': '1' },
            });
            if (response.status === 200) {
                setFileSuccessfullyImported(response.data);
            }
        } catch (error) {
            if (axios.isCancel(error)) {
                console.log('Request canceled:', error.message);
            } else {
                console.error('Error uploading file:', (error as any)?.response?.data?.error);
                setResErrorMessage((error as any)?.response?.data?.error);
            }
        } finally {
            setUploading(false);
            setFiles([]);
            setSelectedTypeOfImport('');
        }
    };

    const handleSelectTypeOfImportChange = (event: SelectChangeEvent, file: any) => {
        const value = event.target.value;
        file.importType = value;
        setSelectedTypeOfImport(value);
    };

    const disableImportBtn = () => {
        let hasOrders = false;
        let hasMaterials = false;
        let hasActions = false;
        let allFilesHaveImportType = true;

        for (const file of files) {
            if (!file.importType) {
                allFilesHaveImportType = false;
                break;
            }
            if (file.importType === 'orders') hasOrders = true;
            if (file.importType === 'materials') hasMaterials = true;
            if (file.importType === 'actions') hasActions = true;
        }

        const isValidCombination = hasOrders && (hasMaterials || hasActions);
        return files.length > 4 || !selectedTypeOfImport || !allFilesHaveImportType || !isValidCombination;
    };

    const disableMenuItem = (value: string) => {
        return files.some((file: { importType?: string }) => file.importType === value);
    };

    return (
        <Fragment>
            <Dialog fullWidth maxWidth="lg" open={open} onClose={onCloseDialog}>
                <DialogTitle>{translate('users:IMPORT_INFORMATION')}</DialogTitle>
                {resErrorMessage && (
                    <Box mb={3} sx={{ width: '100%' }}>
                        <Alert severity="error">{resErrorMessage}</Alert>
                    </Box>
                )}
                {uploading ? (
                    <Loader />
                ) : (
                    <Fragment>
                        <DialogContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FileDropBoxArea accept={accept} handleChange={handleChange} />
                                </Grid>
                                {files.length ? (
                                    <Grid item xs={12}>
                                        <Typography variant="h3">{translate('common:FILES')}</Typography>
                                        <List dense>
                                            {files.map((file: any) => (
                                                <ListItem key={file.path}>
                                                    <ListItemAvatar>
                                                        <Avatar>
                                                            <InsertDriveFileIcon />
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText primary={file.path} />
                                                    <FormControl sx={{ width: '20%' }}>
                                                        <InputLabel>{translate('users:TYPE_OF_IMPORT')}</InputLabel>
                                                        <Select
                                                            value={file.importType}
                                                            label={translate('users:TYPE_OF_IMPORT')}
                                                            onChange={(e) => handleSelectTypeOfImportChange(e, file)}
                                                        >
                                                            <MenuItem
                                                                disabled={disableMenuItem('orders')}
                                                                value="orders"
                                                            >
                                                                {translate('orders:ORDERS')}
                                                            </MenuItem>
                                                            <MenuItem
                                                                disabled={disableMenuItem('actions')}
                                                                value="actions"
                                                            >
                                                                {translate('orders:ACTIONS')}
                                                            </MenuItem>
                                                            <MenuItem
                                                                disabled={disableMenuItem('materials')}
                                                                value="materials"
                                                            >
                                                                {translate('orders:MATERIALS')}
                                                            </MenuItem>
                                                            <MenuItem
                                                                disabled={disableMenuItem('messages')}
                                                                value="messages"
                                                            >
                                                                {translate('orders:MESSAGES')}
                                                            </MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Grid>
                                ) : undefined}
                                {fileSuccessfullyImported && !uploading && (
                                    <AlertSnackbar type="success" message={translate('users:SUCCESSFULL_IMPORT')} />
                                )}
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button disabled={disableImportBtn()} onClick={handleImport}>
                                {translate('users:IMPORT')}
                            </Button>
                            <Button onClick={onCloseDialog}>{translate('common:CLOSE')}</Button>
                        </DialogActions>
                    </Fragment>
                )}
            </Dialog>
        </Fragment>
    );
};

export default ImportOrderFromExternalSystemDialog;
